/**
 * WeaveIn Staff v2 — Sidebar
 * Dynamic role-shaped nav driven by /api/organizations/{orgId}/nav-config.
 * Falls back to hardcoded sections if API unavailable.
 *
 * Connects to org branding, provides role-preview for super admins,
 * and resolves page access per role via backend nav-config endpoint.
 */
import { memo, useState, useCallback, useEffect, useMemo } from 'react'
import {
  ChevronLeft, ChevronRight, ChevronDown, LogOut, Eye, EyeOff, Lock,
  Calendar, Settings, Search, User, Library,
} from 'lucide-react'
import { resolveNavIcon } from '@/components/v2/navIcons'
import { useBranding } from '@/contexts/BrandingContext'
import { useAppTranslation } from '@/contexts/TranslationContext'
import { CopilotLauncher } from '@/components/copilot/CopilotLauncher'
import { NotificationBell } from '@/components/ui/NotificationBell'
import { OfflinePanel } from '@/components/offline/OfflinePanel'
import { ProgramSwitcher } from '@/components/programs/ProgramSwitcher'
import { useOfflineState } from '@/lib/offline/hooks'
import { useNavConfig, type NavConfigSection, type NavConfigItem } from '@/hooks/useNavConfig'
import { resolveNavLabel, resolveNavSectionLabel } from '@/utils/navLabel'
import { useCapability } from '@/hooks/useCapabilities'
import { PAGE_SWITCH, usePageSwitch } from '@/src/app/workforce/hubs/usePageSwitch'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface V2SidebarProps {
  activeTab: string
  onTabChange: (tab: string) => void
  organizationId?: number | null
  /** Active org context — drives employer/sponsor nav sections in V2. */
  orgType?: 'cbo' | 'employer' | 'sponsor'
  userName?: string
  userRole?: string
  orgName?: string
  userInitials?: string
  onShowProfile?: () => void
  /** Open the in-app assistant. Absent = no launcher, which is correct for a
   *  caller the backend has no tools for. */
  onOpenCopilot?: () => void
  /** What this org calls its assistant, from /copilot/capabilities. */
  assistantName?: string
  onLogout?: () => void
  onCollapsedChange?: (collapsed: boolean) => void
  previewRole?: string | null
  /** role_terminology.id of the preview role. Preferred over `previewRole`
   *  (the role_type string) when resolving nav-config: custom org roles can
   *  only be scoped server-side by id, so passing this makes the previewed
   *  sidebar match the nav-layout admin (which also scopes by id). */
  previewRoleId?: number | null
  onPreviewRoleChange?: (role: string | null) => void
  /** Roles to preview as. Pass {value, label} to show org-specific
   *  display names (e.g. "Economic Justice Manager") instead of raw
   *  slugs. The legacy `string[]` shape is also accepted; entries are
   *  coerced to {value:role, label:role}. */
  availableRoles?: Array<{ value: string; label: string }> | string[]
  counts?: {
    myDay?: number
    inbox?: number
    referrals?: number
  }
  /** Shell controls for the header row, beside the collapse toggle (e.g. "Open in…"). */
  headerActions?: React.ReactNode
  /** Cmd/Ctrl+click or middle-click on an item: open that page in a new tab of this window. */
  onOpenInNewTab?: (tab: string) => void
}

// ---------------------------------------------------------------------------
// Count mapping — maps nav_tab_id to count keys
// ---------------------------------------------------------------------------

function getCount(tabId: string, counts?: V2SidebarProps['counts']): { count?: number; variant?: 'danger' | 'neutral' } {
  if (!counts) return {}
  switch (tabId) {
    case 'v2-home': return { count: counts.myDay, variant: 'danger' }
    case 'workspace-messaging': return { count: counts.inbox, variant: 'danger' }
    case 'v2-referrals': return { count: counts.referrals, variant: 'neutral' }
    default: return {}
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function V2SidebarInner({
  activeTab,
  onTabChange,
  organizationId,
  orgType,
  userName = 'Maria Gonzalez',
  userRole = 'Promotora',
  orgName = 'La Casa CBO',
  userInitials = 'MG',
  onShowProfile,
  onOpenCopilot,
  assistantName = 'Wren',
  onLogout,
  onCollapsedChange,
  previewRole,
  previewRoleId,
  onPreviewRoleChange,
  availableRoles,
  counts,
  headerActions,
  onOpenInNewTab,
}: V2SidebarProps) {
  const [collapsed, setCollapsed] = useState(false)
  const [logoError, setLogoError] = useState(false)

  const toggleCollapsed = useCallback(() => {
    const next = !collapsed
    setCollapsed(next)
    onCollapsedChange?.(next)
  }, [collapsed, onCollapsedChange])
  // Per-section collapse. Independently expandable nav groups, keyed by
  // section_key and persisted so a user's layout survives reloads. Empty set =
  // all expanded (the default). Only applies when the rail itself is expanded —
  // in the 60px rail there are no section headers, so items always show.
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set()
    try {
      const raw = window.localStorage.getItem('v2:sidebar:collapsedSections')
      return raw ? new Set<string>(JSON.parse(raw)) : new Set()
    } catch {
      return new Set()
    }
  })

  const toggleSection = useCallback((sectionKey: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(sectionKey)) next.delete(sectionKey)
      else next.add(sectionKey)
      try {
        window.localStorage.setItem(
          'v2:sidebar:collapsedSections',
          JSON.stringify([...next]),
        )
      } catch {
        /* ignore persistence errors (private mode, quota) */
      }
      return next
    })
  }, [])

  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [isOnline, setIsOnline] = useState(true)
  // Offline-service state drives the status dot: amber = manually "Work Offline",
  // gray = actually offline, emerald = online. Reflects the toggle, not just the network.
  const offlineState = useOfflineState()
  const dotForced = offlineState.forcedOffline
  const dotOnline = offlineState.isOnline && !offlineState.forcedOffline
  const [syncModalOpen, setSyncModalOpen] = useState(false)

  // Dynamic nav config — resolves sections + items from backend.
  // When no preview is active, send no role_type so the backend resolves the
  // user's primary org role (role_terminology.id) and applies org-role-scoped
  // overrides. Preview-as still forwards the system role string.
  const previewingDifferentRole = previewRole && previewRole !== userRole
  const { sections } = useNavConfig(
    organizationId || null,
    previewingDifferentRole ? previewRole : undefined,
    orgType,
    // Prefer the numeric role id when previewing so custom org roles (which are
    // not valid system role_types) resolve the same layout the admin editor shows.
    previewingDifferentRole ? (previewRoleId ?? null) : null
  )

  // LCC "Training & Workforce" consolidation. For orgs with the `lms`
  // capability (currently only LCC / cbo 350), fold the five individual
  // LMS/workforce sidebar items into two integrated hub pages under a dedicated
  // "Training & Workforce" section. Purely a sidebar-presentation transform —
  // the underlying tabs/pages still exist and are rendered by the hubs.
  const { enabled: lmsEnabled } = useCapability(organizationId || null, 'lms')
  // TRUS-562: the staff training program (Amigos) uses the same two hubs, so they
  // must not depend on `lms` alone — turning `lms` off at Amigos used to remove its
  // whole training program from the sidebar. LCC holds `lms` and not
  // `staff_training`, so nothing changes for it.
  const { enabled: staffTrainingEnabled } = useCapability(organizationId || null, 'staff_training')
  const navRole = String((previewingDifferentRole ? previewRole : userRole) || '').toLowerCase()
  // Every Training-hub tab is for managers and up; in a staff-training org a plain
  // staff member would open it to an empty page, so it is left out of their sidebar.
  // Per-role switches from the Permission Matrix (TRUS-562). They only ever hide;
  // an org without staff_training (LCC) is never affected by them.
  const switchRole = previewingDifferentRole ? String(previewRole) : undefined
  const learningSwitch = usePageSwitch(organizationId || null, PAGE_SWITCH.learningHub, switchRole)
  const trainingSwitch = usePageSwitch(organizationId || null, PAGE_SWITCH.trainingHub, switchRole)
  const showLearningHub = !learningSwitch.switchedOff
  const showTrainingHub =
    !trainingSwitch.switchedOff &&
    (!staffTrainingEnabled || ['super_admin', 'org_admin', 'ops_admin', 'program_manager', 'supervisor'].includes(navRole))
  const displaySections = useMemo<NavConfigSection[]>(() => {
    if (!lmsEnabled && !staffTrainingEnabled) return sections
    const HIDE = new Set([
      'workforce-learn',
      'workforce-courses',
      'workforce-curricula',
      'workforce-certifications',
      'workforce-training-schedule',
    ])
    const hostKey = sections.find((s) =>
      s.items.some((it) => HIDE.has(it.nav_tab_id)),
    )?.section_key
    const stripped = sections
      .map((s) => ({ ...s, items: s.items.filter((it) => !HIDE.has(it.nav_tab_id)) }))
      .filter((s) => s.items.length > 0)
    const item = (
      nav_tab_id: string,
      label: string,
      icon: string,
      order: number,
    ): NavConfigItem => ({
      page_key: nav_tab_id,
      nav_tab_id,
      label,
      icon,
      has_access: true,
      display_behavior: 'show',
      display_order: order,
      pinned: false,
    })
    const hubSection: NavConfigSection = {
      section_key: 'training_workforce',
      section_name: 'Training & Workforce',
      display_order: 0,
      items: [
        ...(showLearningHub ? [item('workforce-learning-hub', 'Learning', 'Library', 1)] : []),
        ...(showTrainingHub ? [item('workforce-training-hub', 'Training', 'Calendar', 2)] : []),
      ],
    }
    // Anchor the hub section directly below Community Programs; fall back to
    // the section that originally hosted the LMS items, then to the end.
    const communityIdx = stripped.findIndex((s) => s.section_key === 'community_programs')
    const idx =
      communityIdx >= 0
        ? communityIdx
        : hostKey
        ? stripped.findIndex((s) => s.section_key === hostKey)
        : -1
    // Both hubs switched off for this role: no empty "Training & Workforce" heading.
    if (hubSection.items.length === 0) return stripped
    if (idx < 0) return [...stripped, hubSection]
    return [...stripped.slice(0, idx + 1), hubSection, ...stripped.slice(idx + 1)]
  }, [sections, lmsEnabled, staffTrainingEnabled, showLearningHub, showTrainingHub])

  const { branding, refreshBranding } = useBranding()
  const { t, language } = useAppTranslation()

  // Online/offline presence
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    setIsOnline(navigator.onLine)
    return () => { window.removeEventListener('online', handleOnline); window.removeEventListener('offline', handleOffline) }
  }, [])

  // Use org branding for name and logo — refresh on mount to catch recent uploads
  useEffect(() => { refreshBranding?.() }, [])
  const brandName = branding?.brand_name || orgName || 'WeaveIn'
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  // brand_logo_url = primary logo (for light backgrounds)
  // brand_logo_dark_url = dark-mode variant (for dark backgrounds)
  // Same priority: primary variant → fallback → favicon
  const logoUrl = isDark
    ? (branding?.brand_logo_dark_url || branding?.brand_logo_url || branding?.brand_favicon_url)
    : (branding?.brand_logo_url || branding?.brand_favicon_url)

  const canPreview = userRole === 'super_admin' || userRole === 'ops_admin'
  const isPreviewActive = canPreview && previewRole

  const handleNav = useCallback(
    (tabId: string) => {
      onTabChange(tabId)
    },
    [onTabChange],
  )

  return (
    <div
      className={`flex h-screen flex-col border-r border-v2border bg-paper ${collapsed ? 'w-[60px]' : 'w-[240px]'} transition-[width] duration-150`}
      style={{ padding: 12, gap: 8, overflowY: 'auto' }}
    >
      {/* Brand + collapse */}
      <div className={`flex items-center px-1.5 pb-2 pt-1 ${collapsed ? 'flex-col gap-1' : 'justify-between'}`}>
        {!collapsed && (
          <div className="flex min-w-0 items-center gap-2 font-display text-sm font-semibold text-ink">
            {logoUrl && !logoError ? (
              <img
                src={logoUrl}
                alt={brandName}
                className={`h-[22px] w-auto max-w-[80px] flex-shrink-0 object-contain ${!isDark ? 'drop-shadow-[0_0_1px_rgba(0,0,0,0.4)]' : ''}`}
                onError={() => setLogoError(true)}
              />
            ) : (
              <span className="relative flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded bg-ink">
                <span className="absolute inset-1 rounded-sm bg-paper" />
              </span>
            )}
            <span className="truncate" title={brandName}>{brandName}</span>
          </div>
        )}
        <div className={`flex flex-shrink-0 items-center gap-0.5 ${collapsed ? 'flex-col' : ''}`}>
          {/* TRUS-759: the bell sits in the header, in both widths. In the footer it
              was a 14px icon that disappeared when the sidebar was collapsed. */}
          <div data-testid="v2-sidebar-bell" className="[&_button]:h-7 [&_button]:w-7 [&_button]:p-0 [&_svg]:h-4 [&_svg]:w-4 [&_span]:-right-1 [&_span]:-top-1 [&_span]:h-4 [&_span]:min-w-4 [&_span]:w-auto [&_span]:px-1 [&_span]:text-[9px]">
            <NotificationBell onNavigate={onTabChange} />
          </div>
          {headerActions}
          <button
            onClick={toggleCollapsed}
            className="rounded-[var(--v2-r-sm)] p-1 text-ink-400 hover:bg-paper-2 hover:text-ink"
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>
      </div>

      {/* Role preview banner (super admin only) */}
      {!collapsed && isPreviewActive && (
        <div className="flex items-center gap-1.5 rounded-[var(--v2-r)] bg-attention-soft px-2.5 py-1.5 text-[10px] font-medium text-attention-ink">
          <Eye size={10} />
          {/* Not "Viewing as": this changes the menu only. The API still answers as
              you. To see a real person's screens, use User Management → View as
              (TRUS-747). */}
          <span title="Changes the navigation only. Data and permissions are still yours.">
            Role preview (nav only): {previewRole}
          </span>
          <button
            onClick={() => onPreviewRoleChange?.(null)}
            className="ml-auto rounded-sm p-0.5 hover:bg-attention/20"
            title="Exit preview"
          >
            <EyeOff size={10} />
          </button>
        </div>
      )}

      {/* Active program. Renders nothing unless the org holds `program_switcher` AND
          has two or more active programs, so this is invisible for every other org. */}
      <ProgramSwitcher collapsed={collapsed} />

      {/* Search trigger — modifier shown and dispatched matches the host OS
          (Cmd on Mac, Ctrl on Windows/Linux). The global handler accepts
          either modifier, so this is purely a UI affordance. */}
      <div className="flex items-center gap-1.5">
      <button
        className="flex h-[34px] min-w-0 flex-1 items-center gap-2 rounded-[var(--v2-r)] border border-v2border bg-surface px-3 text-[13px] text-ink-400 hover:border-ink-400 hover:text-ink-700"
        onClick={() => {
          const isMac =
            typeof navigator !== 'undefined' &&
            /Mac|iPod|iPhone|iPad/.test(navigator.userAgent)
          const event = new KeyboardEvent('keydown', {
            key: 'k',
            metaKey: isMac,
            ctrlKey: !isMac,
          })
          document.dispatchEvent(event)
        }}
      >
        <Search size={12} />
        {!collapsed && (
          <>
            <span className="flex-1 text-left">Jump to anything…</span>
            <span className="flex gap-0.5">
              <kbd className="flex h-4 min-w-[20px] items-center justify-center rounded-[3px] border border-v2border bg-surface px-1 font-mono text-[10px] text-ink-500">
                {typeof navigator !== 'undefined' &&
                /Mac|iPod|iPhone|iPad/.test(navigator.userAgent)
                  ? '⌘'
                  : 'Ctrl'}
              </kbd>
              <kbd className="flex h-4 min-w-[16px] items-center justify-center rounded-[3px] border border-v2border bg-surface px-1 font-mono text-[10px] text-ink-500">
                K
              </kbd>
            </span>
          </>
        )}
      </button>

      {/* The assistant launcher. Its own component (components/copilot) so the
          same control can appear wherever chrome exists later and carry its
          label rules with it.

          Rendered only when a caller can use it — `onOpenCopilot` is absent for
          anyone the backend has no tools for, and a launcher whose first answer
          is a shrug is worse than no launcher. */}
      {onOpenCopilot && (
        <CopilotLauncher onOpen={onOpenCopilot} assistantName={assistantName} />
      )}
      </div>

      {/* Dynamic nav sections */}
      {displaySections.map((section) => {
        const isSectionCollapsed = collapsedSections.has(section.section_key)
        return (
        <div key={section.section_key}>
          {!collapsed && (
            <button
              onClick={() => toggleSection(section.section_key)}
              aria-expanded={!isSectionCollapsed}
              className="group flex w-full items-center gap-1 rounded-[var(--v2-r-sm)] px-2 pb-1 pt-3 font-display text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-400 hover:text-ink-700"
            >
              <ChevronDown
                size={11}
                className={`flex-shrink-0 text-ink-300 transition-transform duration-150 group-hover:text-ink-500 ${isSectionCollapsed ? '-rotate-90' : ''}`}
              />
              <span className="flex-1 text-left">
                {resolveNavSectionLabel(
                  {
                    serverName: section.section_name,
                    serverNameEs: section.section_name_es,
                    sectionKey: section.section_key,
                  },
                  language,
                  t,
                )}
              </span>
            </button>
          )}
          {(collapsed || !isSectionCollapsed) && section.items.map((item) => {
            const tabId = item.nav_tab_id
            const isActive = activeTab === tabId
            const isDisabled = !item.has_access && item.display_behavior === 'disable'
            const Icon = resolveNavIcon(item.icon)
            const { count, variant } = getCount(tabId, counts)
            const itemLabel = resolveNavLabel(
              {
                serverLabel: item.label,
                serverLabelEs: item.label_es,
                bundleKey: `nav.item.${tabId}`,
                fallback: item.page_key,
              },
              language,
              t,
            )
            return (
              <button
                key={item.page_key}
                onClick={(e) => {
                  if (isDisabled) return
                  if (onOpenInNewTab && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault()
                    onOpenInNewTab(tabId)
                    return
                  }
                  handleNav(tabId)
                }}
                onAuxClick={(e) => {
                  if (e.button !== 1 || isDisabled || !onOpenInNewTab) return
                  e.preventDefault()
                  onOpenInNewTab(tabId)
                }}
                disabled={isDisabled}
                // The activity tracker reads these to name the page a user opened
                // (the browser title is always "WeaveIn") — TRUS-664.
                data-nav-tab={tabId}
                data-nav-label={itemLabel}
                aria-current={isActive ? 'page' : undefined}
                className={`flex w-full items-center gap-2.5 rounded-[var(--v2-r-sm)] px-[9px] py-[7px] text-[13px] font-medium transition-colors duration-75 ${
                  isDisabled
                    ? 'cursor-not-allowed text-ink-300 opacity-50'
                    : isActive
                      ? 'bg-[#14171c] text-white dark:bg-white dark:text-[#14171c]'
                      : 'text-ink-700 hover:bg-paper-2 hover:text-ink'
                } ${collapsed ? 'justify-center' : ''}`}
                title={collapsed ? itemLabel : isDisabled ? 'Access restricted' : undefined}
              >
                {isDisabled ? <Lock size={14} className="flex-shrink-0" /> : <Icon size={14} className="flex-shrink-0" />}
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">{itemLabel}</span>
                    {count != null && count > 0 && !isDisabled && (
                      <span
                        className={`min-w-[18px] rounded-[10px] px-1.5 py-px text-center font-display text-[10px] font-semibold ${
                          isActive
                            ? 'bg-white/20 text-white dark:bg-black/10 dark:text-[#14171c]'
                            : variant === 'neutral'
                              ? 'bg-paper-2 text-ink-500'
                              : 'bg-danger text-white'
                        }`}
                      >
                        {count}
                      </span>
                    )}
                  </>
                )}
              </button>
            )
          })}
        </div>
        )
      })}

      {/* Footer — user presence + menu */}
      <div className="relative mt-auto border-t border-v2border px-2 pb-1 pt-3">
        <div className={`flex items-center gap-2.5 ${collapsed ? 'justify-center' : ''}`}>
          <button
            type="button"
            onClick={() => setSyncModalOpen(true)}
            className="relative inline-flex cursor-pointer rounded-full transition hover:opacity-80"
            title={`${dotForced ? 'Working offline' : dotOnline ? 'Online' : 'Offline'} — click to view sync status`}
          >
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-ink font-display text-[11px] font-semibold text-white">
              {userInitials}
            </span>
            <span className={`absolute -bottom-px -right-px h-2.5 w-2.5 rounded-full border-2 border-paper ${dotForced ? 'bg-amber-500' : dotOnline ? 'bg-emerald-500' : 'bg-gray-400'}`} />
          </button>
          <OfflinePanel isOpen={syncModalOpen} onClose={() => setSyncModalOpen(false)} />
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-semibold text-ink">{userName}</div>
              <div className="text-[10px] text-ink-500">
                {isPreviewActive ? previewRole : userRole} · {orgName}
              </div>
            </div>
          )}
          {!collapsed && (
            <div className="flex items-center gap-0.5">
              {/* Settings menu */}
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="rounded-[var(--v2-r-sm)] p-1 text-ink-400 hover:bg-paper-2 hover:text-ink"
              >
                <Settings size={14} />
              </button>
            </div>
          )}
        </div>

        {/* User dropdown menu */}
        {userMenuOpen && !collapsed && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
            <div className="absolute bottom-full left-2 right-2 z-50 mb-2 overflow-hidden rounded-[var(--v2-r-lg)] border border-v2border bg-surface shadow-[var(--v2-shadow-2)]">
              <button
                onClick={() => { setUserMenuOpen(false); onShowProfile?.() }}
                className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-[12px] font-medium text-ink-700 hover:bg-paper"
              >
                <User size={13} /> My profile
              </button>

              {/* Role preview section (super_admin / ops_admin only) */}
              {canPreview && availableRoles && availableRoles.length > 0 && (
                <div className="border-t border-v2border-muted">
                  <div className="px-3 pb-1 pt-2 font-mono text-[9px] uppercase tracking-[0.12em] text-ink-400">
                    Preview role (nav only)
                  </div>
                  <button
                    onClick={() => { onPreviewRoleChange?.(null); setUserMenuOpen(false) }}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[11px] ${!previewRole ? 'font-semibold text-ink' : 'text-ink-500 hover:bg-paper'}`}
                  >
                    <EyeOff size={11} /> My role ({userRole})
                  </button>
                  {(availableRoles as Array<string | { value: string; label: string }>).map((role) => {
                    const value = typeof role === 'string' ? role : role.value;
                    const label = typeof role === 'string' ? role : role.label;
                    return (
                      <button
                        key={value}
                        onClick={() => { onPreviewRoleChange?.(value); setUserMenuOpen(false) }}
                        className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[11px] ${previewRole === value ? 'font-semibold text-ink' : 'text-ink-500 hover:bg-paper'}`}
                      >
                        <Eye size={11} /> {label}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="border-t border-v2border-muted">
                <button
                  onClick={() => { setUserMenuOpen(false); onLogout?.() }}
                  className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-[12px] font-medium text-danger hover:bg-danger-soft"
                >
                  <LogOut size={13} /> Sign out
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export const V2Sidebar = memo(V2SidebarInner)
export default V2Sidebar
